import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { BadgeCheck, MapPin, Trees, MessageCircle, ArrowRight, ArrowDown } from "lucide-react";
import { IMAGES, waLink } from "@/lib/brand";

const line = {
  hidden: { y: "110%" },
  show: (i) => ({
    y: "0%",
    transition: { duration: 0.9, delay: 0.25 + i * 0.12, ease: [0.22, 1, 0.36, 1] },
  }),
};

const NOTIFICATIONS = [
  { icon: BadgeCheck, title: "Nuovo acquirente compatibile", tone: "Match trovato", coords: { top: "4%", left: "-4%" }, delay: 1.1 },
  { icon: MapPin, title: "Ricerca attiva a Corato", tone: "In tempo reale", coords: { top: "34%", right: "-2%" }, delay: 1.35 },
  { icon: Trees, title: "Richiesto spazio esterno", tone: "Preferenza cliente", coords: { bottom: "22%", left: "-4%" }, delay: 1.6 },
];

function Notification({ icon: Icon, title, tone, coords, delay }) {
  return (
    <div
      className="notif absolute z-20"
      style={{ ...coords, animationDelay: `${delay}s` }}
    >
      <div
        className="notif-inner flex items-center gap-3 rounded-2xl bg-white border border-border-subtle ring-1 ring-brand/5 shadow-[0_12px_40px_rgba(15,92,74,0.28)] pl-3 pr-4 py-2.5"
        style={{ animationDelay: `${delay}s` }}
      >
        <span className="grid place-items-center h-9 w-9 rounded-xl bg-brand/10 text-brand">
          <Icon className="h-4 w-4" strokeWidth={2} />
        </span>
        <span className="leading-tight">
          <span className="block text-[13px] font-semibold text-anthracite">{title}</span>
          <span className="block text-[11px] text-brand font-medium">{tone}</span>
        </span>
      </div>
    </div>
  );
}

export default function Hero({ onValuation, onScrollBuyers }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const words = ["Vendi", "il tuo", "immobile"];

  return (
    <section id="top" ref={ref} className="relative pt-28 sm:pt-32 pb-20 overflow-hidden">
      <div className="mx-auto max-w-[1400px] px-6 lg:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          {/* LEFT */}
          <div className="lg:col-span-6">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="inline-flex items-center gap-2 text-xs sm:text-sm uppercase tracking-[0.22em] font-semibold text-brand mb-6"
            >
              <span className="h-px w-6 bg-brand/50" />
              Casamore • Servizi immobiliari a Corato
            </motion.div>

            <h1 aria-label="Vendi il tuo immobile" className="font-display font-semibold text-anthracite text-6xl sm:text-7xl lg:text-8xl leading-[0.92] tracking-tight">
              {words.map((w, i) => (
                <span key={w} className="block overflow-hidden">
                  <motion.span
                    className="block"
                    variants={line}
                    custom={i}
                    initial="hidden"
                    animate="show"
                  >
                    {w === "immobile" ? <span className="italic text-brand">{w}</span> : w}
                  </motion.span>
                </span>
              ))}
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, duration: 0.8 }}
              className="mt-7 max-w-md text-lg leading-relaxed text-anthracite/70"
            >
              Non limitarti a pubblicarlo. Scopri come valorizzarlo, presentarlo e venderlo
              nelle migliori condizioni.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.05, duration: 0.8 }}
              className="mt-8 flex flex-col sm:flex-row gap-3"
            >
              <button
                onClick={onValuation}
                className="group inline-flex items-center justify-center gap-2 rounded-full bg-brand text-white font-semibold px-7 py-4 transition-all hover:bg-brand-hover hover:scale-[1.03]"
                data-testid="hero-valuation-cta"
              >
                Scopri quanto può valere
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </button>
              <a
                href={waLink("Salve Pierluigi, vorrei parlare della vendita del mio immobile.")}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-full border border-brand text-brand font-semibold px-7 py-4 transition-all hover:bg-brand hover:text-white"
                data-testid="hero-whatsapp-cta"
              >
                <MessageCircle className="h-4 w-4" />
                Parla con Pierluigi
              </a>
            </motion.div>

            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.25, duration: 0.8 }}
              className="mt-5 text-sm text-anthracite/55"
            >
              Prima analisi gratuita <span className="text-brand mx-1.5">•</span> Valutazione personalizzata
              <span className="text-brand mx-1.5">•</span> Risposta diretta
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2, duration: 0.8 }}
              className="mt-8 flex flex-wrap items-center gap-4 rounded-2xl border border-border-subtle bg-white/60 px-5 py-4"
            >
              <span className="text-sm text-anthracite/70">
                Stai cercando casa?
              </span>
              <Link
                to="/immobili"
                className="group inline-flex items-center gap-2 text-sm font-semibold text-brand hover:text-brand-hover transition-colors"
                data-testid="hero-immobili-cta"
              >
                Scopri gli immobili
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </motion.div>
          </div>

          {/* RIGHT */}
          <div className="lg:col-span-6 relative">
            <div className="relative">
              <motion.div
                initial={{ opacity: 0, scale: 1.05 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
                className="relative rounded-[2rem] overflow-hidden aspect-[4/5] sm:aspect-[5/6] shadow-[0_30px_80px_-20px_rgba(15,92,74,0.35)]"
              >
                <motion.img
                  src={IMAGES.hero}
                  alt="Immobile di pregio a Corato"
                  style={{ y: imgY }}
                  className="absolute inset-0 w-full h-[120%] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-anthracite/25 via-transparent to-transparent" />
              </motion.div>

              {NOTIFICATIONS.map((n) => (
                <Notification key={n.title} {...n} />
              ))}

              {/* Agent placeholder card */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.4, duration: 0.7 }}
                className="absolute -bottom-8 left-1/2 -translate-x-1/2 z-30 w-[88%] sm:w-auto"
              >
                <div className="flex items-center gap-4 rounded-2xl bg-white border border-border-subtle shadow-[0_15px_40px_-10px_rgba(15,92,74,0.25)] px-4 py-3.5">
                  <span className="grid place-items-center h-14 w-14 shrink-0 rounded-full bg-brand text-white font-display text-2xl font-semibold">
                    PA
                  </span>
                  <span className="leading-tight">
                    <span className="block text-sm text-anthracite/70 italic">
                      "Analizzerò personalmente la tua richiesta."
                    </span>
                    <span className="block text-[15px] font-semibold text-brand mt-0.5">Pierluigi Amorese</span>
                  </span>
                </div>
              </motion.div>
            </div>
          </div>
        </div>

        {/* Secondary buyer teaser */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-24 sm:mt-16 rounded-3xl bg-brand text-white px-8 py-10 sm:px-12 sm:py-12 flex flex-col lg:flex-row lg:items-center gap-6 justify-between"
        >
          <p className="font-display text-3xl sm:text-4xl leading-tight max-w-2xl">
            La tua casa potrebbe essere proprio quella che uno dei nostri clienti sta cercando.
          </p>
          <button
            onClick={onScrollBuyers}
            className="group shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-white text-brand font-semibold px-7 py-4 transition-all hover:scale-[1.03]"
            data-testid="hero-buyer-teaser-cta"
          >
            Scopri se abbiamo già il tuo acquirente
            <ArrowDown className="h-4 w-4 transition-transform group-hover:translate-y-1" />
          </button>
        </motion.div>
      </div>
    </section>
  );
}
